import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const Field = ({ label, children }) => (<div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>);
const Pick = ({ value, onChange, options, testid }) => (
  <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export default function OnboardDialog({ open, onOpenChange, prefill = {}, onDone }) {
  const blank = { name: "", email: "", phone: "", zips: "", kind: "rep", sam_role: "affiliate", main_role: "technician", own_subaccount: false };
  const [f, setF] = useState({ ...blank, ...prefill });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const isRep = f.kind === "rep";
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/onboarding", { ...f, application_id: prefill.application_id });
      const bad = Object.entries(data.steps || {}).filter(([, s]) => !s.ok).map(([k]) => k);
      bad.length ? toast.warning(`Set up with issues: ${bad.join(", ")}. Use Retry in the checklist.`) : toast.success(`${data.name} is fully set up`);
      onOpenChange(false); onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="onboard-dialog">
        <DialogHeader><DialogTitle>{prefill.application_id ? "Approve & set up" : "Add person"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={f.name} onChange={set("name")} data-testid="onboard-name" /></Field>
          <Field label="Email"><Input value={f.email} onChange={set("email")} data-testid="onboard-email" /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={set("phone")} data-testid="onboard-phone" /></Field>
          <Field label="Type"><Pick value={f.kind} onChange={set("kind")} testid="onboard-kind" options={[["rep", "Team member / sales rep"], ["owner", "Business owner"], ["agency", "Agency owner"]]} /></Field>
          {isRep && <Field label="Site role"><Pick value={f.sam_role} onChange={set("sam_role")} testid="onboard-sam-role" options={[["affiliate", "Sales rep (dashboard)"], ["staff", "Staff (admin tools)"]]} /></Field>}
          {isRep && <Field label="Main CRM workspace"><Pick value={f.main_role} onChange={set("main_role")} testid="onboard-main-role" options={[["technician", "Technician (own leads)"], ["staff", "Staff (all leads)"], ["none", "Don't add"]]} /></Field>}
          <div className="col-span-2"><Field label="Assigned ZIPs (comma separated)"><Input value={f.zips} onChange={set("zips")} placeholder="65721, 65810" data-testid="onboard-zips" /></Field></div>
          {isRep && <label className="col-span-2 flex items-center gap-2 text-sm"><Switch checked={f.own_subaccount} onCheckedChange={set("own_subaccount")} data-testid="onboard-own-sub" />Also create their own CRM sub-account</label>}
          {!isRep && <p className="col-span-2 text-xs text-sam-slate">Business and agency owners always get their own CRM sub-account with the owner credit cap.</p>}
        </div>
        <Button onClick={submit} disabled={busy || !f.name || !f.email} className="bg-sam-green hover:bg-sam-green/90" data-testid="onboard-submit">
          {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Set up everything
        </Button>
      </DialogContent>
    </Dialog>
  );
}
